import React, { useEffect, useRef } from 'react';
import {
  Box,
  Flex,
  IconButton,
  Text,
  useColorModeValue,
  useDisclosure,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Stack,
  Button,
  Link,
  Image,
} from '@chakra-ui/react';
import { HamburgerIcon } from '@chakra-ui/icons';
import { FaUser, FaSignOutAlt } from 'react-icons/fa';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import routes from '../routes.js';
import IconBox from './Icons/IconBox';
import { Separator } from './Separator/Separator';

const MobileBottomNav = () => {
  console.log('MobileBottomNav rendering...');
  const { logout, hasRole, hasAnyRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const prevPathRef = useRef(location.pathname);
  
  // Colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const iconColor = useColorModeValue('gray.600', 'gray.300');
  const activeIconColor = useColorModeValue('teal.500', 'teal.300');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const activeBg = useColorModeValue("white", "gray.700");
  const inactiveBg = useColorModeValue("white", "gray.700");
  const activeColor = useColorModeValue("gray.700", "white");
  const inactiveColor = useColorModeValue("gray.400", "gray.400");

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleLogout = () => {
    console.log('Logout clicked');
    if (window.confirm('آیا از خروج از سیستم اطمینان دارید؟')) {
      logout();
      navigate('/auth/signin');
    }
  };

  // بستن سایدبار هنگام تغییر route
  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathRef.current;
    
    console.log('Location changed:', currentPath, 'prevPath:', prevPath, 'isOpen:', isOpen);
    
    // فقط اگر مسیر واقعاً تغییر کرده و سایدبار باز است، آن را ببند
    if (currentPath !== prevPath && isOpen) {
      console.log('Closing sidebar due to route change');
      onClose();
    }
    
    prevPathRef.current = currentPath;
  }, [location.pathname, isOpen, onClose]);

  // فیلتر کردن مسیرها بر اساس نقش کاربر
  const filterRoutesByRole = (routes) => {
    return routes.filter(route => {
      if (!route.roles) {
        return true;
      }
      return hasAnyRole(route.roles);
    }).map(route => {
      if (route.views) {
        return {
          ...route,
          views: filterRoutesByRole(route.views)
        };
      }
      return route;
    });
  };

  // verifies if routeName is the one active
  const activeRoute = (routeName) => {
    return location.pathname === routeName ? "active" : "";
  };

  const createLinks = (routes) => {
    return routes.map((prop, key) => {
      if (prop.redirect) {
        return null;
      }
      if (prop.category) {
        return (
          <div key={prop.name}>
            <Text
              color={activeColor}
              fontWeight="bold"
              mb="12px"
              mx="auto"
              ps="16px"
              py="12px"
            >
              {prop.rtlName || prop.name}
            </Text>
            {createLinks(prop.views)}
          </div>
        );
      }
      return (
        <NavLink to={prop.path} key={prop.name}>
          {activeRoute(prop.path) === "active" ? (
            <Button
              boxSize="initial"
              justifyContent="flex-start"
              alignItems="center"
              bg={activeBg}
              mb="12px"
              mx="auto"
              ps="16px"
              py="12px"
              borderRadius="15px"
              _hover="none"
              w="100%"
              _active={{
                bg: "inherit",
                transform: "none",
                borderColor: "transparent",
              }}
              _focus={{
                boxShadow: "none",
              }}
            >
              <Flex>
                <IconBox
                  bg="teal.300"
                  color="white"
                  h="30px"
                  w="30px"
                  me="12px"
                >
                  {prop.icon}
                </IconBox>
                <Text color={activeColor} my="auto" fontSize="sm">
                  {prop.rtlName || prop.name}
                </Text>
              </Flex>
            </Button>
          ) : (
            <Button
              boxSize="initial"
              justifyContent="flex-start"
              alignItems="center"
              bg="transparent"
              mb="12px"
              mx="auto"
              py="12px"
              ps="16px"
              borderRadius="15px"
              _hover="none"
              w="100%"
              _active={{
                bg: "inherit",
                transform: "none",
                borderColor: "transparent",
              }}
              _focus={{
                boxShadow: "none",
              }}
            >
              <Flex>
                <IconBox
                  bg={inactiveBg}
                  color="teal.300"
                  h="30px"
                  w="30px"
                  me="12px"
                >
                  {prop.icon}
                </IconBox>
                <Text color={inactiveColor} my="auto" fontSize="sm">
                  {prop.rtlName || prop.name}
                </Text>
              </Flex>
            </Button>
          )}
        </NavLink>
      );
    });
  };

  // فیلتر کردن مسیرها
  const filteredRoutes = filterRoutesByRole(routes);
  const links = <>{createLinks(filteredRoutes)}</>;

  // Brand
  const brand = (
    <Box pt="25px" mb="12px">
      <Link
        href={`${process.env.PUBLIC_URL}/#/`}
        target="_blank"
        display="flex"
        lineHeight="100%"
        mb="30px"
        fontWeight="bold"
        justifyContent="center"
        alignItems="center"
        fontSize="11px"
      >
        <Image
          src="/logo.png"
          alt="لوگو نارون سبز"
          w="32px"
          h="32px"
          me="10px"
          objectFit="contain"
        />
        <Text fontSize="sm" mt="3px" fontWeight="bold" color="teal.600">
          نارون سبز
        </Text>
      </Link>
      <Separator></Separator>
    </Box>
  );

  return (
    <>
      {/* Mobile Bottom Navigation */}
      <Box
        display={{ base: 'block', lg: 'none' }}
        position="fixed"
        bottom="0"
        left="0"
        right="0"
        zIndex="1000"
        borderTop="1px"
        borderColor={borderColor}
        boxShadow="0 -2px 10px rgba(0, 0, 0, 0.1)"
        backdropFilter="blur(10px)"
        bg={useColorModeValue('rgba(255, 255, 255, 0.95)', 'rgba(26, 32, 44, 0.95)')}
      >
        <Flex justify="space-between" align="center" px={4} py={2}>
          {/* Sidebar Toggle Button - سمت راست */}
          <Flex direction="column" align="center" flex="1">
            <IconButton
              icon={<HamburgerIcon />}
              variant="ghost"
              color={iconColor}
              _hover={{ color: activeIconColor }}
              _active={{ color: activeIconColor }}
              onClick={onOpen}
              aria-label="باز کردن منو"
              size="lg"
              mb={1}
            />
            <Text fontSize="xs" color={textColor} fontWeight="medium">
              منو
            </Text>
          </Flex>

          {/* Profile Button - وسط */}
          <Flex direction="column" align="center" flex="1">
            <IconButton
              icon={<FaUser />}
              variant="ghost"
              color={iconColor}
              _hover={{ color: activeIconColor }}
              _active={{ color: activeIconColor }}
              onClick={handleProfileClick}
              aria-label="پروفایل"
              size="lg"
              mb={1}
            />
            <Text fontSize="xs" color={textColor} fontWeight="medium">
              پروفایل
            </Text>
          </Flex>

          {/* Logout Button - سمت چپ */}
          <Flex direction="column" align="center" flex="1">
            <IconButton
              icon={<FaSignOutAlt />}
              variant="ghost"
              color={iconColor}
              _hover={{ color: 'red.500' }}
              _active={{ color: 'red.500' }}
              onClick={handleLogout}
              aria-label="خروج از سیستم"
              size="lg"
              mb={1}
            />
            <Text fontSize="xs" color={textColor} fontWeight="medium">
              خروج
            </Text>
          </Flex>
        </Flex>
      </Box>

      {/* Custom Mobile Sidebar Drawer */}
      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        placement="right"
        size="sm"
      >
        <DrawerOverlay />
        <DrawerContent
          w="280px"
          maxW="280px"
          ms="16px"
          my="16px"
        >
          <DrawerBody maxW="280px" px="1rem">
            <Box maxW="100%" h="100vh">
              <Box>{brand}</Box>
              <Stack direction="column" mb="40px">
                <Box>{links}</Box>
              </Stack>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
};

export default MobileBottomNav;
